import { MNElement, MNImageContent } from '../../../../common/elements/elementModelTypes';
import { getElementId, getImageProp } from '../../../../common/elements/utils/elementPropertyUtils';
import { MimeType } from '../../../../common/media/mimeConstants';
import { fetchImageBlobFromUrl } from '../../../element/image/imageFetchUtils';
import { getImageSource } from '../../../element/image/imageHelper';
import { loadHtmlImageFromBlob } from '../../../element/image/modal/content/utils/imageModalImageBlobUtils';

// Largest first, with the huge size (capped at IMAGE_WIDTH_MAX) ahead of the original so the download stays
// small enough for the copy to feel immediate. Regular is a 400px thumbnail, so it is only a last resort.
const CLIPBOARD_IMAGE_SIZES = ['huge', 'large', 'original', 'regular'] as const;

/**
 * Browsers only accept PNG image data on the clipboard, so any other format is redrawn to a canvas.
 * Decoding from the downloaded bytes keeps the canvas untainted wherever the image is hosted.
 */
const encodeBlobAsPng = async (blob: Blob): Promise<Blob> => {
    if (blob.type === MimeType.PNG) return blob;

    const source = await loadHtmlImageFromBlob(blob);

    try {
        const canvas = document.createElement('canvas');
        canvas.width = source.image.naturalWidth;
        canvas.height = source.image.naturalHeight;

        const context = canvas.getContext('2d');
        if (!context) throw new Error('No 2d context');

        context.drawImage(source.image, 0, 0);

        const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, MimeType.PNG));
        if (!pngBlob) throw new Error('toBlob returned null');

        return pngBlob;
    } finally {
        source.revoke();
    }
};

/**
 * Downloads an image element's pixels and returns them as PNG for the system clipboard.
 */
export const getElementImagePngBlob = async (
    element: MNElement,
    renderSecureImagesThroughCF: boolean,
): Promise<Blob> => {
    const imageDetails = getImageProp(element) as MNImageContent | undefined;
    const imageSize = CLIPBOARD_IMAGE_SIZES.find((size) => imageDetails?.[size]);
    if (!imageSize) throw new Error('Image element has no downloadable source');

    const imageUrl = getImageSource({
        imageDetails,
        imageSize,
        elementId: getElementId(element),
        useSecureMediaUrl: true,
        renderSecureImagesThroughCF,
        optimizeForWebRender: false,
    });
    if (!imageUrl) throw new Error('Unable to resolve image source for clipboard');

    return encodeBlobAsPng(await fetchImageBlobFromUrl(imageUrl));
};
